import { AgoraRTCReactError, ICameraVideoTrack, IMicrophoneAudioTrack } from "agora-rtc-react";
import { useCallback, useEffect, useState } from "react";

export interface LocalCameraTrack extends ICameraVideoTrack {
  getMediaStreamTrackSettings(): Pick<MediaDeviceInfo, 'deviceId'>;
}

export interface LocalMicrophoneTrack extends IMicrophoneAudioTrack {
  getMediaStreamTrackSettings(): Pick<MediaDeviceInfo, 'deviceId'>;
}

export type ChangeDeviceProps = string | {
  deviceId: MediaDeviceInfo['deviceId'],
} | {
  facingMode: VideoFacingModeEnum,
}

// Hook is used to change the device used for the local track safely, e.g.,
// switching the front-facing camera of a mobile device to the back-facing.  
export default function useChangeDeviceForLocalTrack(
  track: LocalCameraTrack | LocalMicrophoneTrack,
  isLoading: boolean,
  error: AgoraRTCReactError | null
) {
  const [device, setDevice] = useState<MediaDeviceInfo['deviceId']>();

  const isTrackReady = track && !isLoading && !error;

  // Resolves false when the device did not actually change — the track was not
  // ready, or the switch was refused (another app holds the camera, or the
  // constraint cannot be met). Callers that record what the camera is doing
  // must not believe a switch that never happened.
  const changeDevice = useCallback(async (options: ChangeDeviceProps): Promise<boolean> => {
    if (!isTrackReady) {
      return false;
    }
    let changed = true;
    try {
      // mic track supports only deviceId passed, so we are adding this
      // to get around ts lint issues
      if (track.trackMediaType === 'video') {
        const cameraTrack = track as LocalCameraTrack;
        await cameraTrack?.setDevice(options);
      } else {
        await track?.setDevice(options as string);
      }
    } catch {
      console.log(`Failed to change ${track.getTrackId()}:${track.getTrackLabel()} device`);
      changed = false;
    }

    const newDeviceId = track?.getMediaStreamTrackSettings().deviceId;
    setDevice(newDeviceId);
    return changed;
  }, [track, isTrackReady]);

  useEffect(() => {
    if (!isTrackReady) {
      return;
    }

    setDevice(track.getMediaStreamTrackSettings().deviceId);
  }, [track, isTrackReady]);

  return {
    device,
    changeDevice
  }
}
